import React, { useState } from 'react';
import { DevFillButton, DEVFILL_TID } from 'src/devtools/DevFillButton';
import { DEVFILL_ENABLED } from 'src/devtools/gate';
import { fakeSignerName } from 'src/devtools/fillers/w2';

import { Checkbox } from '@components/ui/checkbox';

import type { ContractDocument, ContractRenderData } from '../api/contract';
import { W2_CONTRACT_TID } from '../constants/testids';
import { formatDisplayDate, todayISO } from '../format';
import { ContractDocumentView } from './ContractDocument';

interface ContractPendingProps {
  data: ContractRenderData & { document: ContractDocument };
  signing: boolean;
  signError: string | null;
  onSign: (input: { signerName: string; federalTaxId: string }) => void;
}

const TID = W2_CONTRACT_TID.pending;

/**
 * Shape mask only — the server validates. Keeps what is typed in the `NN-NNNNNNN` form the
 * document renders, so the live-filled §1 row never shows a half-formed value.
 */
const maskEin = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 9);
  return digits.length > 2
    ? `${digits.slice(0, 2)}-${digits.slice(2)}`
    : digits;
};

const LABEL_CLASS = 'text-[13px] text-[#5a5a70]';

const INPUT_CLASS =
  'mb-2 w-full max-w-[420px] rounded-[10px] border border-[#b7b7b7] px-3 py-3.5' +
  ' text-[16px] outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <h2 className="text-xs font-semibold uppercase tracking-wider text-[#8a8aa0] mb-3">
    {children}
  </h2>
);

/**
 * Pending state: the server's contract document, followed by the signing block. The
 * attestation checkbox gates the sign action; the signature is the typed full legal name;
 * the date is today by definition (shown read-only, sent as `date` in the sign body).
 *
 * The tax ID and name typed here are live-filled into the document's slots and travel only
 * in the sign request — no payload the page receives contains either, and the allowlist
 * normalizer keeps bank/ACH data out of the render entirely.
 */
export const ContractPending: React.FC<ContractPendingProps> = ({
  data,
  signing,
  signError,
  onSign,
}) => {
  const [signerName, setSignerName] = useState('');
  const [federalTaxId, setFederalTaxId] = useState('');
  const [authorized, setAuthorized] = useState(false);

  const signDate = formatDisplayDate(todayISO());
  // The document's §13 signature block carries the attestation the checkbox gates.
  const attestation = data.document.sections
    .flatMap((section) => section.blocks)
    .find((block) => block.kind === 'signature')
    ?.attestation.join(' ');
  const canSign =
    authorized &&
    signerName.trim() !== '' &&
    // A partial tax ID is a guaranteed server rejection — gate on the full nine digits.
    /^\d{2}-\d{7}$/.test(federalTaxId) &&
    !signing;

  return (
    <div data-testid={TID.root} className="flex flex-col gap-8 max-sm:gap-6">
      {DEVFILL_ENABLED && (
        <DevFillButton
          testid={DEVFILL_TID.contractSign}
          label="Fill signature"
          onFill={async () => {
            setSignerName(await fakeSignerName());
            setFederalTaxId('84-1234567');
            setAuthorized(true);
          }}
        />
      )}
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#8a8aa0] mb-2">
          Group contract
        </p>
        <h1
          data-testid={TID.company}
          className="text-3xl font-extrabold text-[#1a1a2e] max-sm:text-2xl"
        >
          {data.companyName}
        </h1>
        <p className="mt-2 text-[15px] leading-6 text-[#5a5a70]">
          Review the agreement below and sign to put the group coverage for{' '}
          {data.companyName} in place. What you see here is exactly the document
          that will be filed once signed.
        </p>
      </header>

      <ContractDocumentView
        document={data.document}
        slotValues={{
          federalTaxId: federalTaxId.trim(),
          signerName: signerName.trim(),
          signatureName: signerName.trim(),
        }}
      />

      <section className="rounded-xl border border-[#ececf2] bg-white p-6 max-sm:p-4">
        <SectionLabel>Sign the agreement</SectionLabel>
        <div className="flex flex-col gap-2">
          <label className={LABEL_CLASS} htmlFor="contract-federal-tax-id">
            Federal Tax ID Number (EIN)
          </label>
          <input
            id="contract-federal-tax-id"
            type="text"
            inputMode="numeric"
            placeholder="12-3456789"
            value={federalTaxId}
            onChange={(e) => setFederalTaxId(maskEin(e.target.value))}
            data-testid={TID.ein}
            className={INPUT_CLASS}
          />
          <label className={LABEL_CLASS} htmlFor="contract-signer-name">
            Type your full legal name to sign
          </label>
          <input
            id="contract-signer-name"
            type="text"
            autoComplete="name"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value.replace(/^\s+/, ''))}
            data-testid={TID.signerName}
            className={INPUT_CLASS}
          />
          <p
            data-testid={TID.signDate}
            className="mt-2 text-[14px] text-[#5a5a70]"
          >
            Date:{' '}
            <span className="font-semibold text-[#1a1a2e]">{signDate}</span>
          </p>

          <div className="mb-2 mt-2 flex items-start gap-3">
            <Checkbox
              id="contract-attestation"
              data-testid={TID.attestation}
              checked={authorized}
              onCheckedChange={(checked) => setAuthorized(checked === true)}
              className="size-6 xl:size-6 mt-0.5"
            />
            <label
              htmlFor="contract-attestation"
              data-testid={TID.attestationLabel}
              className="text-[15px] leading-6 text-[#1a1a2e] cursor-pointer"
            >
              {/* The wording is the document's own §13 attestation — page copy
                  would be a second source of the thing being agreed to. */}
              {attestation}
            </label>
          </div>

          {signError && (
            <p
              data-testid={TID.signError}
              role="alert"
              className="text-[14px] leading-5 text-[#c0392b]"
            >
              {signError}
            </p>
          )}

          <button
            type="button"
            data-testid={TID.signButton}
            disabled={!canSign}
            onClick={() =>
              onSign({
                signerName: signerName.trim(),
                federalTaxId: federalTaxId.trim(),
              })
            }
            className="self-start rounded-full bg-primary px-8 py-3 text-[15px] font-semibold text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed max-sm:w-full"
          >
            {signing ? 'Signing…' : 'Sign contract'}
          </button>

          {/* Copy that must never drift from the server's TTL — the number is the
              resolve's `linkValidDays`, never hardcoded here. */}
          <p
            data-testid={TID.validity}
            className="mt-3 text-[13px] leading-5 text-[#5a5a70]"
          >
            Your typed name and Tax ID fill into Sections 1 and 13 above as you
            type. On signing, this exact document is stored as the executed
            contract PDF and a copy is emailed to you.
            {data.linkValidDays !== undefined &&
              ` This link is valid for ${data.linkValidDays} days.`}
          </p>
        </div>
      </section>
    </div>
  );
};
